import { supabase } from './supabase';
import type { WeddingTimelineEvent, WeddingVendor } from '../types/dayof';

export async function getTimelineEvents(): Promise<WeddingTimelineEvent[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { data, error } = await supabase
      .from('wedding_timeline_events')
      .select('*')
      .eq('user_id', user.id)
      .order('time', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching timeline events:', error);
    throw error;
  }
}

export async function addTimelineEvent(event: Omit<WeddingTimelineEvent, 'id'>): Promise<WeddingTimelineEvent | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { data, error } = await supabase
      .from('wedding_timeline_events')
      .insert([{
        ...event,
        user_id: user.id
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding timeline event:', error);
    throw error;
  }
}

export async function updateTimelineEvent(id: string, updates: Partial<WeddingTimelineEvent>): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_timeline_events')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating timeline event:', error);
    throw error;
  }
}

export async function deleteTimelineEvent(id: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_timeline_events')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting timeline event:', error);
    throw error;
  }
}

export async function getVendors(): Promise<WeddingVendor[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { data, error } = await supabase
      .from('wedding_vendors')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw error;
  }
}

export async function addVendor(vendor: Omit<WeddingVendor, 'id' | 'user_id' | 'created_at'>): Promise<WeddingVendor | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { data, error } = await supabase
      .from('wedding_vendors')
      .insert([{
        ...vendor,
        user_id: user.id
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding vendor:', error);
    throw error;
  }
}

export async function updateVendor(id: string, updates: Partial<WeddingVendor>): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_vendors')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating vendor:', error);
    throw error;
  }
}

export async function deleteVendor(id: string): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated');

    const { error } = await supabase
      .from('wedding_vendors')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting vendor:', error);
    throw error;
  }
}

export async function getUserRegistryDate(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('registries')
      .select('event_date')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) return null;
    return data.event_date;
  } catch (error) {
    console.error('Error fetching registry date:', error);
    return null;
  }
}

export async function getUserGuestList(): Promise<string[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: registry, error: registryError } = await supabase
      .from('registries')
      .select('id')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (registryError || !registry) return [];

    const { data: guestTables, error: guestError } = await supabase
      .from('guest_tables')
      .select('guest_name')
      .eq('registry_id', registry.id)
      .order('guest_name', { ascending: true });

    if (guestError) throw guestError;
    return guestTables?.map(guest => guest.guest_name) || [];
  } catch (error) {
    console.error('Error fetching guest list:', error);
    return [];
  }
}
